import React, { useMemo } from 'react';

interface MiniSparklineProps {
  /** Array of percentage values (0–100) */
  data: number[];
  width?: number;
  height?: number;
  className?: string;
}

function getTrendColor(data: number[]): string {
  if (data.length < 2) return 'hsl(var(--muted-foreground))';
  // Compare last 30% of points vs earlier to determine trend
  const splitIdx = Math.max(1, Math.floor(data.length * 0.7));
  const recent = data.slice(splitIdx);
  const earlier = data.slice(Math.max(0, splitIdx - recent.length), splitIdx);

  const avgRecent = recent.reduce((s, v) => s + v, 0) / recent.length;
  const avgEarlier = earlier.reduce((s, v) => s + v, 0) / earlier.length;
  const delta = avgRecent - avgEarlier;

  if (delta > 2) return 'hsl(var(--success))';     // progressing
  if (delta < -2) return 'hsl(var(--destructive))'; // decelerating
  return 'hsl(var(--warning))';                      // stable
}

export const MiniSparkline: React.FC<MiniSparklineProps> = ({
  data,
  width = 80,
  height = 24,
  className,
}) => {
  const { polyline, color } = useMemo(() => {
    if (!data || data.length < 2) return { polyline: '', color: 'hsl(var(--muted-foreground))' };

    const color = getTrendColor(data);
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const pad = 1;
    const w = width - pad * 2;
    const h = height - pad * 2;

    const points = data.map((v, i) => {
      const x = pad + (i / (data.length - 1)) * w;
      const y = pad + h - ((v - min) / range) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });

    return { polyline: points.join(' '), color };
  }, [data, width, height]);

  if (!data || data.length < 2) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden="true"
    >
      <polyline
        points={polyline}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
